import React, { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { type LadderStep } from './OvertimeLadderVisual';
import { ControlRow } from '../ControlRow';
import { ExtendedOvertimeSection } from '../ExtendedOvertimeSection';
import { AnimatePresence, motion } from 'framer-motion';
import { EASE_EXPO_OUT, DURATION_MEDIUM } from '@/lib/motion';
import type { ExtendedTier } from '@/types/ExtendedTier';
import { SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING } from './helpSheetOpticalSpacing';

const OT_METHODS = ['Carry Over', 'Bump Up', 'Offset Overtime'] as const;

interface OvertimeLadderComparisonProps {
  ladder: LadderStep[];
  extendedTiers?: LadderStep[];
  extendedOvertimeEnabled?: boolean;
  onExtendedOvertimeToggle?: (enabled: boolean) => void;
  offsetTierIndex: number;
  bracketLabels: string[];
  methodName: string;
  onConfigureExtended?: () => void;
  onAddExtendedTier?: () => void;
  editableExtendedTiers?: ExtendedTier[];
  onEditableExtendedTiersChange?: (tiers: ExtendedTier[]) => void;
  editableExtendedTiersContext?: 'user' | 'client';
  editableExtendedTiersMax?: number;
  belowGraphContent?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

/** Convert a range bracket like "10–14 hrs" to an open-ended label "10+ hrs". */
const toOpenEndedLabel = (bracket: string): string => {
  const m = bracket.match(/^(\d+(?:\.\d+)?)\s*[–-]/);
  return m ? `${m[1]}+ hrs` : bracket;
};

const ChevronSeparator: React.FC<{ color: string }> = ({ color }) => (
  <div className="h-2" aria-hidden="true">
    <div className={cn('h-full w-full', color)} />
  </div>
);

const EmptyExtendedPlaceholder: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <button
    type="button"
    data-anim-audit="short-turn-ot-empty-extended-placeholder"
    onClick={onClick}
    className={cn(
      'w-full rounded border border-dashed border-primary/25 bg-primary/[0.03]',
      'px-2 py-3 text-center',
      onClick && 'cursor-pointer hover:border-primary/40 hover:bg-primary/[0.06] transition-colors',
    )}
  >
    <span className="font-body italic" style={{ fontSize: 11, color: 'color-mix(in oklab, var(--color-muted-foreground) 60%, transparent)' }}>
      Add tiers to extend your ladder
    </span>
  </button>
);

/* ------------------------------------------------------------------ */
/*  Subcomponents                                                      */
/* ------------------------------------------------------------------ */

type CardVariant = 'dimmed' | 'neutral' | 'active' | 'capped' | 'extended-active';

const variantClasses: Record<CardVariant, string> = {
  dimmed: 'opacity-30 border-border/60 bg-surface-2 dark:bg-transparent [border-top-color:rgba(255,255,255,0.12)]',
  neutral: 'border-border/60 bg-surface-2 dark:bg-transparent [border-top-color:rgba(255,255,255,0.12)]',
  active:
    'border-primary/60 bg-surface-2 dark:bg-transparent shadow-[0_0_6px_rgba(232,134,12,0.18),inset_0_1px_2px_rgba(0,0,0,0.12)]',
  capped:
    'opacity-85 border-destructive/30 bg-destructive/[0.06] shadow-[inset_0_1px_2px_rgba(0,0,0,0.12)]',
  'extended-active':
    'border-dashed border-primary/60 bg-primary/[0.05] shadow-[0_0_6px_rgba(232,134,12,0.18),inset_0_1px_2px_rgba(0,0,0,0.12)]',
};

const isTierBadgeText = (value: string): boolean =>
  value === 'Base' || /^Tier\s+\d+$/i.test(value) || /^Ext\s+\d+$/i.test(value);

const ComparisonTierCard: React.FC<{
  step: LadderStep;
  badge: string;
  variant: CardVariant;
  hours?: string;
  subtitle?: string;
  isEntry?: boolean;
  className?: string;
  stretched?: boolean;
  compact?: boolean;
  tierBadge?: string;
  inlineNameRow?: boolean;
}> = (props) => {
  const {
    step,
    badge,
    variant,
    className,
    stretched,
    compact,
    tierBadge,
    inlineNameRow,
  } = props;
  const visuallyCenterTallCappedName = Boolean(stretched && !inlineNameRow && !compact && variant === 'capped');
  const rateEl = (
    <span
      className={cn(
        'justify-self-start font-mono text-xs font-bold tabular-nums',
        variant === 'dimmed'
          ? 'text-primary-text line-through'
          : variant === 'capped'
            ? 'text-destructive-text'
            : 'text-primary-text',
      )}
    >
      {step.rate}x
    </span>
  );

  const nameEl = (
    <span
      className={cn(
        'block w-full text-center font-heading text-[10px] sm:text-[12px] uppercase tracking-wide truncate',
        variant === 'dimmed'
          // Parent card already applies opacity-30, so compound-dimming the
          // text and decoration washes the strikethrough out in dark mode.
          // Let the parent alone carry the dimming and keep the line-through
          // at currentColor so it stays visible.
          ? 'text-foreground line-through'
          : variant === 'capped'
          ? 'text-destructive-text'
          : variant === 'extended-active'
            ? 'text-primary-text'
            : 'text-foreground/80',
      )}
    >
      {step.name}
    </span>
  );

  const showTierBadge = Boolean(tierBadge && !isTierBadgeText(tierBadge));
  const showBadge = !isTierBadgeText(badge);

  const badgeEl = (showTierBadge && showBadge) ? (
    <div className="justify-self-end flex flex-col items-end gap-1">
      <span className="inline-flex items-center rounded px-2 py-1 text-[10px] sm:text-[12px] font-mono font-semibold leading-none bg-primary/15 text-primary-text">
        {tierBadge}
      </span>
      <span className="inline-flex items-center rounded px-2 py-1 text-[10px] sm:text-[12px] font-mono font-semibold leading-none bg-destructive/15 text-destructive-text">
        {badge}
      </span>
    </div>
  ) : showBadge ? (
    <span
      className={cn(
        'justify-self-end inline-flex items-center rounded px-2 py-1 text-[10px] sm:text-[12px] font-mono font-semibold leading-none',
        variant === 'capped'
          ? 'bg-destructive/15 text-destructive-text'
          : variant === 'extended-active'
            ? 'border border-dashed border-primary/40 bg-primary/[0.08] text-primary-text'
            : 'bg-primary/15 text-primary-text',
      )}
    >
      {badge}
    </span>
  ) : showTierBadge ? (
    <span className="justify-self-end inline-flex items-center rounded px-2 py-1 text-[10px] sm:text-[12px] font-mono font-semibold leading-none bg-primary/15 text-primary-text">
      {tierBadge}
    </span>
  ) : null;

  if (stretched) {
    if (inlineNameRow) {
      return (
        <div style={{ gap: 0 }} className={cn('w-full rounded border flex flex-col', variantClasses[variant], className)}>
          <div className="grid w-full grid-cols-[1fr_2fr_1fr] items-center gap-2 px-2 py-2">
            {rateEl}
            {nameEl}
            {badgeEl}
          </div>
        </div>
      );
    }

    return (
      <div style={{ gap: 0 }} className={cn('w-full rounded border flex flex-col relative', variantClasses[variant], className)}>
        <div style={{ gap: 0 }} className={cn('flex w-full items-center justify-between px-2', compact ? 'pt-1' : 'pt-2')}>
          {rateEl}
          {showTierBadge && showBadge ? <div className="absolute top-2 right-2">{badgeEl}</div> : badgeEl}
        </div>
        <div
          className={cn(
            'w-full grid place-items-center px-2',
            !compact && !visuallyCenterTallCappedName && 'flex-1',
            visuallyCenterTallCappedName && 'absolute inset-0',
          )}
        >
          {nameEl}
        </div>
      </div>
    );
  }

  return (
    <div style={{ gap: 0 }} className={cn('w-full rounded border flex flex-col', variantClasses[variant], className)}>
      <div className="grid w-full grid-cols-[1fr_2fr_1fr] items-center gap-2 px-2 py-2">
        {rateEl}
        {nameEl}
        {badgeEl}
      </div>
    </div>
  );
};

/** Renders extended tier cards with chevron separators. */
const ExtendedTierStack: React.FC<{
  tiers: LadderStep[];
  resolveBracket: (step: LadderStep, idx: number) => string | undefined;
  showEntryOnFirst?: boolean;
}> = ({ tiers, resolveBracket, showEntryOnFirst }) => (
  <div>
    {tiers.map((step, idx) => (
      <React.Fragment key={`ext-${step.name}`}>
        {idx > 0 && <ChevronSeparator color="text-primary-text/60" />}
        <ComparisonTierCard
          step={step}
          badge={`Ext ${idx + 1}`}
          variant="extended-active"
          hours={resolveBracket(step, idx)}
          subtitle="Extended"
          isEntry={showEntryOnFirst && idx === 0}
          stretched
          compact
          inlineNameRow
        />
      </React.Fragment>
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const tierBadgeLabel = (idx: number) => idx === 0 ? 'Base' : `Tier ${idx}`;

export const OvertimeLadderComparison: React.FC<OvertimeLadderComparisonProps> = ({
  ladder,
  extendedTiers,
  extendedOvertimeEnabled,
  onExtendedOvertimeToggle,
  offsetTierIndex,
  bracketLabels,
  methodName,
  onConfigureExtended,
  onAddExtendedTier,
  editableExtendedTiers,
  onEditableExtendedTiersChange,
  editableExtendedTiersContext,
  editableExtendedTiersMax,
  belowGraphContent,
}) => {
  // Warn dev builds when the caller wires the toggle callback without the
  // paired `extendedOvertimeEnabled` flag. Fires once per prop change rather
  // than on every render.
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && onExtendedOvertimeToggle && extendedOvertimeEnabled === undefined) {
      console.warn('OvertimeLadderComparison: extendedOvertimeEnabled is required when onExtendedOvertimeToggle is provided');
    }
  }, [onExtendedOvertimeToggle, extendedOvertimeEnabled]);

  // Precedence rule: when the toggle is provided AND enabled flag is explicitly set,
  // use the flag as source of truth. Otherwise fall back to extendedTiers as-is.
  // This prevents partial prop wiring from silently suppressing saved tiers.
  const activeExtendedTiers = (onExtendedOvertimeToggle && extendedOvertimeEnabled !== undefined)
    ? (extendedOvertimeEnabled ? extendedTiers : undefined)
    : extendedTiers;
  const resolvedMethod = OT_METHODS.includes(methodName as (typeof OT_METHODS)[number])
    ? methodName : OT_METHODS[0];
  const inlineEditorConfigureRef = useRef<(() => void) | null>(null);
  const hasEmbeddedTierEditor = Boolean(onExtendedOvertimeToggle && extendedOvertimeEnabled !== undefined);
  const hasExternalEditableBinding = Boolean(editableExtendedTiers && onEditableExtendedTiersChange);
  const derivedInlineTiers = useMemo<ExtendedTier[]>(
    () => (activeExtendedTiers ?? []).map((tier, idx) => ({
      name: tier.name,
      rate: tier.rate,
      sortOrder: idx + 1,
    })),
    [activeExtendedTiers],
  );
  const [internalInlineTiers, setInternalInlineTiers] = useState<ExtendedTier[]>(derivedInlineTiers);

  useEffect(() => {
    if (!hasExternalEditableBinding) {
      setInternalInlineTiers(derivedInlineTiers);
    }
  }, [derivedInlineTiers, hasExternalEditableBinding]);

  const inlineEditorTiers = hasExternalEditableBinding
    ? (editableExtendedTiers ?? [])
    : internalInlineTiers;
  const handleInlineTierChange = (tiers: ExtendedTier[]) => {
    if (hasExternalEditableBinding) {
      onEditableExtendedTiersChange?.(tiers);
      return;
    }
    setInternalInlineTiers(tiers);
  };
  const canAddExtendedTier = Boolean(onAddExtendedTier || onConfigureExtended || hasEmbeddedTierEditor);
  const handleAddExtendedTier = canAddExtendedTier
    ? () => {
      if (onAddExtendedTier) {
        onAddExtendedTier();
        return;
      }
      if (hasEmbeddedTierEditor && inlineEditorConfigureRef.current) {
        inlineEditorConfigureRef.current();
        return;
      }
      onConfigureExtended?.();
    }
    : undefined;

  // Buffer methodName so the ladder fades out old content before rendering new
  const [displayMethod, setDisplayMethod] = useState(resolvedMethod);
  const [fading, setFading] = useState(false);
  const gridWrapperRef = useRef<HTMLDivElement>(null);

  /**
   * Lock current height, fade out, swap content, fade in, animate to new height, then reset.
   *
   * Height is a layout property so browsers can't GPU-accelerate the
   * transition, but the lock/release pattern is the idiomatic way to morph
   * between two non-zero content heights (grid-template-rows 0fr↔1fr only
   * covers collapse/expand). We hint will-change: height during the
   * ~250ms transition window so the browser can optimize, and drop the hint
   * afterwards to avoid persistent layer promotion.
   */
  const lockHeight = () => {
    const el = gridWrapperRef.current;
    if (el) {
      el.style.willChange = 'height';
      el.style.height = `${el.offsetHeight}px`;
    }
  };
  const releaseHeight = () => {
    requestAnimationFrame(() => {
      const el = gridWrapperRef.current;
      if (el) {
        el.style.height = `${el.scrollHeight}px`;
        setTimeout(() => {
          const current = gridWrapperRef.current;
          if (current) {
            current.style.height = '';
            current.style.willChange = '';
          }
        }, 250);
      }
    });
  };

  // Crossfade when methodName prop changes (normalized to valid options)
  const prevMethodRef = useRef(resolvedMethod);
  useEffect(() => {
    if (prevMethodRef.current !== resolvedMethod) {
      prevMethodRef.current = resolvedMethod;
      lockHeight();
      setFading(true);
      const timer = setTimeout(() => {
        setDisplayMethod(resolvedMethod);
        setFading(false);
        releaseHeight();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [resolvedMethod]);

  if (ladder.length === 0) return null;

  const clampedOffset = Math.min(offsetTierIndex, ladder.length - 1);
  const scenarioTier = ladder[clampedOffset];
  const topTier = ladder[ladder.length - 1];

  const entryLabel = bracketLabels[0] ?? '0+ hrs';
  const subsequentBrackets = bracketLabels.slice(1);
  const cappedBracket = subsequentBrackets[0] ?? '10+ hrs';
  const mergedTopTierHoursLabel = entryLabel === 'All hours' ? 'All hours' : '0+ hrs';
  const leftCappedHoursLabel = entryLabel === 'All hours'
    ? 'All hours'
    : toOpenEndedLabel(cappedBracket);

  // Reachable extended tiers = tiers skipped by the offset (those below the entry point).
  const maxReachable = clampedOffset;
  const reachableExtTiers = activeExtendedTiers?.slice(0, maxReachable);
  const hasExtended = reachableExtTiers && reachableExtTiers.length > 0;
  const shouldMergeTopTierLeft = clampedOffset === ladder.length - 1;
  const dimmedTiers = ladder.slice(0, clampedOffset);

  /** Resolve bracket label for the last extended tier to an open-ended label. */
  const resolveSubsequentBracket = (_step: LadderStep, idx: number) => {
    const bracket = subsequentBrackets[idx];
    if (idx === (reachableExtTiers?.length ?? 0) - 1 && bracket) return toOpenEndedLabel(bracket);
    return bracket;
  };

  /*
   * Single grid with 2 columns. Each visual "row" renders a left cell and a
   * right cell so they stay horizontally aligned through the shared portion.
   * After the offset marker the columns diverge (left: capped, right: extended).
   */
  return (
    <div
      data-anim-audit="short-turn-ot-comparison-root"
      data-anim-audit-method={displayMethod}
      data-anim-audit-scenario-tier={scenarioTier.name}
      data-anim-audit-offset-tier-index={clampedOffset}
      data-anim-audit-extended-enabled={String(Boolean(extendedOvertimeEnabled ?? true))}
      data-anim-audit-reachable-extended-count={reachableExtTiers?.length ?? 0}
    >
      <div
        ref={gridWrapperRef}
        className="overflow-hidden mb-2 transition-[height] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
        data-anim-audit="short-turn-ot-grid-wrapper"
      >
      <div
        className="grid grid-cols-2 gap-x-2 gap-y-0 transition-opacity duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
        data-anim-audit="short-turn-ot-grid"
        style={{ opacity: fading ? 0 : 1 }}
      >
        <p className="col-span-2 font-heading text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.14em] text-foreground/75 mb-2 text-center">
          Surcharge Ladder
        </p>
        <p className="font-heading text-[10px] sm:text-[12px] leading-none uppercase tracking-wider text-muted-foreground/70 mb-2 text-center">
          Without Extended Tiers
        </p>
        <p className="font-heading text-[10px] sm:text-[12px] leading-none uppercase tracking-wider text-primary-text mb-2 text-center">
          With Your Extended Tiers
        </p>

        {dimmedTiers.map((step, idx) => (
          <div key={`dim-${step.name}`} className="col-span-2">
            {idx > 0 && <ChevronSeparator color="text-muted-foreground/40" />}
            <ComparisonTierCard
              step={step}
              badge={tierBadgeLabel(idx)}
              variant={idx === clampedOffset - 1 ? 'neutral' : 'dimmed'}
            />
          </div>
        ))}

        {clampedOffset > 0 && (
          shouldMergeTopTierLeft ? (
            // Offset is at top: split arrows lead into diverging merged-top section
            <>
              <ChevronSeparator color="text-muted-foreground/40" />
              <ChevronSeparator color="text-muted-foreground/40" />
            </>
          ) : (
            // Next item is a full-width merged card: single centered arrow
            <div className="col-span-2">
              <ChevronSeparator color="text-muted-foreground/40" />
            </div>
          )
        )}

        {shouldMergeTopTierLeft ? (
          <div className="col-span-2 grid grid-cols-2 gap-x-2 items-stretch">
            <ComparisonTierCard
              step={topTier}
              badge="Capped"
              tierBadge={tierBadgeLabel(clampedOffset)}
              variant="capped"
              hours={mergedTopTierHoursLabel}
              isEntry
              className="h-full"
              stretched
            />
            <div>
              <ComparisonTierCard
                step={ladder[clampedOffset]}
                badge={tierBadgeLabel(clampedOffset)}
                variant="active"
                hours={entryLabel}
                isEntry
                stretched
                compact
                inlineNameRow
              />
              <ChevronSeparator color="text-primary-text/50" />
              {hasExtended ? (
                <ExtendedTierStack
                  tiers={reachableExtTiers}
                  resolveBracket={resolveSubsequentBracket}
                />
              ) : (
                <EmptyExtendedPlaceholder onClick={handleAddExtendedTier} />
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="col-span-2">
              <ComparisonTierCard
                step={ladder[clampedOffset]}
                badge={tierBadgeLabel(clampedOffset)}
                variant="active"
                hours={entryLabel}
                isEntry
                stretched
                compact
              />
            </div>

            <ChevronSeparator color="text-destructive-text/30" />
            <ChevronSeparator color="text-primary-text/50" />

            {hasExtended ? (
              <div className="col-span-2 grid grid-cols-2 gap-x-2 items-stretch">
                <ComparisonTierCard
                  step={topTier}
                  badge="Capped"
                  tierBadge={tierBadgeLabel(ladder.length - 1)}
                  variant="capped"
                  hours={leftCappedHoursLabel}
                  className="h-full"
                  stretched
                />
                <div>
                  <ComparisonTierCard
                    step={topTier}
                    badge={`Tier ${ladder.length - 1}`}
                    variant="active"
                    hours={subsequentBrackets[0]}
                    isEntry
                    stretched
                    compact
                    inlineNameRow
                  />
                  <ChevronSeparator color="text-primary-text/60" />
                  <ExtendedTierStack
                    tiers={reachableExtTiers}
                    resolveBracket={(_step, idx) => {
                      const bracket = subsequentBrackets[idx + 1];
                      if (idx === reachableExtTiers.length - 1 && bracket) return toOpenEndedLabel(bracket);
                      return bracket;
                    }}
                  />
                </div>
              </div>
            ) : (
              <>
                <ComparisonTierCard
                  step={topTier}
                  badge="Capped"
                  tierBadge={tierBadgeLabel(ladder.length - 1)}
                  variant="capped"
                  hours={leftCappedHoursLabel}
                />
                <EmptyExtendedPlaceholder onClick={handleAddExtendedTier} />
              </>
            )}
          </>
        )}

        <div className="flex items-center justify-center mt-1 gap-1">
          <div className="h-px w-3 bg-destructive/25" />
          <span className="font-mono text-[10px] sm:text-[11px] text-muted-foreground uppercase tracking-wider">
            Pay stuck at ceiling
          </span>
          <div className="h-px w-3 bg-destructive/25" />
        </div>
        <div />{/* grid alignment: empty right cell */}
      </div>
      </div>

      {belowGraphContent && (
        <div className={`${SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING.extendedOvertimeGroup} space-y-2`} data-anim-audit="short-turn-ot-help-below-graph-content">
          {belowGraphContent}
        </div>
      )}

      {onExtendedOvertimeToggle && extendedOvertimeEnabled !== undefined && (
        <div
          className={cn(
            'rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20',
            'mt-3',
          )}
          data-anim-audit="short-turn-ot-help-control-card"
        >
          <ControlRow label="Extended Overtime" description="Add tiers beyond your standard overtime ladder for stronger short-turn surcharges.">
            <Switch
              aria-label="Toggle extended overtime"
              data-anim-audit="short-turn-ot-help-toggle"
              checked={!!extendedOvertimeEnabled}
              onCheckedChange={onExtendedOvertimeToggle}
            />
          </ControlRow>
          <AnimatePresence initial={false}>
            {extendedOvertimeEnabled && (
              <motion.div
                key="extended-tier-list"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
                className="overflow-hidden"
                data-anim-audit="short-turn-ot-help-tier-list-collapse"
              >
                <div
                  className="px-2 py-2"
                  data-anim-audit="short-turn-ot-help-tier-list-shell"
                >
                  <div
                    className="rounded border border-border/60 bg-surface-1/30 p-2 sm:p-2 space-y-2"
                    data-anim-audit="short-turn-ot-help-tier-list"
                  >
                    <ExtendedOvertimeSection
                      mode="editor-only"
                      enabled={true}
                      onEnabledChange={() => {}}
                      tiers={inlineEditorTiers}
                      onTiersChange={handleInlineTierChange}
                      highestOvertimeRate={topTier.rate}
                      highestOvertimeTierName={topTier.name}
                      context={editableExtendedTiersContext ?? 'user'}
                      maxTiers={editableExtendedTiersMax}
                      configureRef={inlineEditorConfigureRef}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
